import React, { useState, useCallback } from "react";
import {
  Box,
  Typography,
  IconButton,
  Chip,
  CircularProgress,
  ListItemIcon,
  MenuItem,
  Menu,
  useTheme,
} from "@mui/material";
import { DataGridPro, GridOverlay } from "@mui/x-data-grid-pro";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import ImageIcon from "@mui/icons-material/Image";
import VideoFileIcon from "@mui/icons-material/VideoFile";
import { useTranslation } from "react-i18next";
import { useCustomLocaleText } from "@/Components/Common/useCustomLocaleText";
import { toast } from "react-toastify";
import { useGetAttachmentUrlMutation } from "@/features/Global/api";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

interface AttachmentListViewProps {
  attachments: any[];
  isLoading: boolean;
  onView: (attachment: any) => void;
  onEdit: (attachment: any) => void;
  onDelete: (attachment: any) => void;
  anchorEl: HTMLElement | null;
  selectedRowForMenu: any;
  onMenuOpen: (event: React.MouseEvent<HTMLElement>, row: any) => void;
  onMenuClose: () => void;
  onFileUpload: (files: File[]) => void;
  isDropZone?: boolean;
  onTranscribe?: (fileId: string, fileName: string) => void;
  transcribingVideos?: Set<string>;
  transcriptionProgress?: Map<string, string>;
}

const AttachmentListView: React.FC<AttachmentListViewProps> = ({
  attachments,
  isLoading,
  onView,
  onEdit,
  onDelete,
  anchorEl,
  selectedRowForMenu,
  onMenuOpen,
  onMenuClose,
  onFileUpload,
  isDropZone,
  onTranscribe,
  transcribingVideos,
  transcriptionProgress,
}) => {
  const { t } = useTranslation(["bugherd"]);
  const theme = useTheme();
  const localeText = useCustomLocaleText();
  const [downloadingItems, setDownloadingItems] = useState<Set<string>>(
    new Set()
  );
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);

      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        onFileUpload(files);
      }
    },
    [onFileUpload]
  );

  const handleBrowseFiles = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.accept = "image/*";
    input.onchange = (e) => {
      const files = Array.from((e.target as HTMLInputElement).files || []);
      if (files.length > 0) {
        onFileUpload(files);
      }
    };
    input.click();
  };

  const handleMenuAction = (action: string) => {
    if (!selectedRowForMenu) return;

    switch (action) {
      case "view":
        onView(selectedRowForMenu);
        break;
      case "edit":
        onEdit(selectedRowForMenu);
        break;
      case "delete":
        onDelete(selectedRowForMenu);
        break;
      case "download":
        handleDownload(selectedRowForMenu);
        break;
    }
    onMenuClose();
  };

  const downloadBase64File = (base64Data: string, fileName: string) => {
    try {
      const link = document.createElement("a");
      link.href = base64Data;
      link.download = fileName || "download";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading base64 file:", error);
      toast.error(t("Failed to download file"));
    }
  };

  const downloadFromUrl = async (url: string, fileName: string) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName || "download";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Error downloading from URL:", error);
      toast.error(t("Failed to download file"));
    }
  };

  const [postAttachmentKey, { isLoading: isGettingUrlLoading }] =
    useGetAttachmentUrlMutation();

  const handleDownload = async (attachment: any) => {
    const itemId = attachment._id;
    setDownloadingItems((prev) => new Set(prev).add(itemId));

    try {
      const fileName = attachment.name || attachment.fileName || "download";

      // Case 1: Base64 data (local files or annotated images)
      if (attachment.url && attachment.url.startsWith("data")) {
        downloadBase64File(attachment.url, fileName);
        toast.success(t("File downloaded successfully"));
        return;
      }

      // Case 2: Direct URL (video recordings, external files)
      if (
        attachment.url &&
        (attachment.url.startsWith("http") ||
          attachment.url.startsWith("blob:"))
      ) {
        await downloadFromUrl(attachment.url, fileName);
        toast.success(t("File downloaded successfully"));
        return;
      }

      if (attachment.url && attachment?.url.startsWith("dev/")) {
        const keyToFetch = attachment.serverUrl || attachment.url;

        const response = await postAttachmentKey({
          key: keyToFetch,
        }).unwrap();

        if (response?.data?.decryptedData?.url) {
          await downloadFromUrl(response.data.decryptedData.url, fileName);
          toast.success(t("File downloaded successfully"));
        } else {
          throw new Error("Failed to get download URL");
        }
        return;
      }

      toast.error(t("No download URL available for this file"));
    } catch (error) {
      console.error("Error downloading attachment:", error);
      toast.error(t("Failed to download file"));
    } finally {
      setDownloadingItems((prev) => {
        const newSet = new Set(prev);
        newSet.delete(itemId);
        return newSet;
      });
    }
  };

  const getFileIcon = (attachment: any) => {
    if (attachment.type === "screenshot") {
      return <ImageIcon color="primary" />;
    } else if (attachment.type === "video") {
      return <VideoFileIcon color="secondary" />;
    } else if (attachment.fileType?.startsWith("image/")) {
      return <ImageIcon color="primary" />;
    } else {
      return <InsertDriveFileIcon />;
    }
  };

  const columns = [
    {
      field: "name",
      headerName: t("Name"),
      flex: 1,
      minWidth: 200,
      renderCell: (params: any) => {
        if (params.row.isDropZone) {
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                height: "100%",
                width: "100%",
                p: 0.5,
                border: `1px dashed ${theme.palette.divider}`,
                borderRadius: 1,
                backgroundColor: isDragOver ? (theme.palette.mode === "dark" ? theme.palette.background.default : "#f5f5f5") : "transparent",
                cursor: "pointer",
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.paper : "#f8f9ff",
                },
              }}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={(e) => {
                e.stopPropagation();
                handleBrowseFiles();
              }}
            >
              <CloudUploadIcon
                sx={{
                  fontSize: 20,
                  color: isDragOver ? theme.palette.primary.main : theme.palette.text.secondary,
                  mr: 0.5,
                }}
              />
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: isDragOver ? theme.palette.primary.main : theme.palette.text.secondary,
                    fontWeight: 500,
                  }}
                >
                  {isDragOver
                    ? t("Drop files here")
                    : t("Drop files to attach or Browse")}
                </Typography>
              </Box>
            </Box>
          );
        }

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              height: "100%",
              cursor: "pointer",
            }}
            onClick={(e) => {
              onView(params.row);
              e.stopPropagation();
            }}
          >
            {React.cloneElement(getFileIcon(params.row), {
              fontSize: "small",
            })}
            <Typography
              variant="body2"
              sx={{ overflow: "hidden", textOverflow: "ellipsis", fontSize: 12 }}
            >
              {params.value}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "type",
      headerName: t("Type"),
      width: 120,
      renderCell: (params: any) => {
        if (params.row.isDropZone) {
          return null;
        }
        return (
          <Chip
            label={params.value}
            size="small"
            variant="outlined"
            color={
              params.value === "screenshot"
                ? "primary"
                : params.value === "video"
                ? "secondary"
                : "default"
            }
            sx={{ height: 20, fontSize: 11 }}
          />
        );
      },
    },
    {
      field: "date",
      headerName: t("Date"),
      width: 150,
      renderCell: (params: any) => {
        if (params.row.isDropZone) {
          return null;
        }
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography variant="body2" sx={{ fontSize: 12 }}>
              {params.value
                ? new Date(params.value).toLocaleDateString()
                : "N/A"}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "actions",
      headerName: t("Actions"),
      width: 80,
      sortable: false,
      renderCell: (params: any) => {
        if (params.row.isDropZone) {
          return null;
        }
        return (
          <IconButton
            size="small"
            onClick={(event) => onMenuOpen(event, params.row)}
            title={t("More actions")}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        );
      },
    },
  ];

  return (
    <>
      <Box sx={{ maxHeight: 600, minHeight: 240, width: "100%" }}>
        <DataGridPro
          getRowId={(row) => row._id}
          columns={columns}
          style={{ maxHeight: 600, minHeight: 240 }}
          rows={[
            ...(isDropZone
              ? [
                  {
                    _id: "drop-zone",
                    name: "",
                    type: "drop-zone",
                    date: null,
                    isDropZone: true,
                  },
                ]
              : []),
            ...attachments,
          ]}
          disableColumnMenu
          loading={isLoading}
          rowSelection={false}
          disableMultipleColumnsSorting
          localeText={localeText}
          hideFooter
          rowHeight={40}
          columnHeaderHeight={40}
          slots={{
            noRowsOverlay: () => (
              <GridOverlay>
                <div>{t("No attachments found")}</div>
              </GridOverlay>
            ),
            loadingOverlay: () => (
              <GridOverlay>
                <CircularProgress />
              </GridOverlay>
            ),
          }}
          sx={{
            "& .MuiDataGrid-cell": {
              borderBottom: `1px solid ${theme.palette.divider}`,
              fontSize: 12,
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.default : "#f5f5f5",
              borderBottom: `2px solid ${theme.palette.divider}`,
              fontSize: 12,
            },
            "& .drop-zone-row": {
              backgroundColor: isDragOver ? (theme.palette.mode === "dark" ? theme.palette.background.default : "#f5f5f5") : "transparent",
              cursor: "pointer",
              "&:hover": {
                backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.paper : "#f8f9ff",
              },
            },
          }}
          getRowClassName={(params) =>
            params.row.isDropZone ? "drop-zone-row" : ""
          }
        />
      </Box>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={onMenuClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem onClick={() => handleMenuAction("view")}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          {t("View")}
        </MenuItem>
        <MenuItem
          onClick={() => handleMenuAction("download")}
          disabled={downloadingItems.has(selectedRowForMenu?._id)}
          sx={{ fontSize: "14px" }}
        >
          <ListItemIcon>
            {downloadingItems.has(selectedRowForMenu?._id) ? (
              <CircularProgress size={16} />
            ) : (
              <DownloadIcon fontSize="small" />
            )}
          </ListItemIcon>
          {downloadingItems.has(selectedRowForMenu?._id)
            ? t("Downloading...")
            : t("Download")}
        </MenuItem>
        {(selectedRowForMenu?.fileType?.startsWith("image/") ||
          selectedRowForMenu?.type === "screenshot") && (
          <MenuItem
            onClick={() => handleMenuAction("edit")}
            disabled={selectedRowForMenu?.isUploading}
          >
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            {t("Edit")}
          </MenuItem>
        )}
        <MenuItem onClick={() => handleMenuAction("delete")}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          {t("Delete")}
        </MenuItem>
      </Menu>
    </>
  );
};

export default AttachmentListView;
